import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { useCustomCursor } from '../hooks/useCustomCursor';
import { useCursorContext } from '../hooks/useCursorContext';

export function CustomCursor() {
  const { position, isVisible, isTouchDevice } = useCustomCursor();
  const { cursorState, cursorText } = useCursorContext();
  const dotRef = useRef<HTMLDivElement>(null);
  const circleRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isTouchDevice || !dotRef.current || !circleRef.current) return;

    // Dot follows immediately
    gsap.to(dotRef.current, {
      x: position.x,
      y: position.y,
      duration: 0,
    });

    // Circle follows with delay
    gsap.to(circleRef.current, {
      x: position.x,
      y: position.y,
      duration: 0.3,
      ease: 'power2.out',
    });
  }, [position, isTouchDevice]);

  useEffect(() => {
    if (!circleRef.current) return;

    // Animate cursor state changes
    if (cursorState === 'hovering' || cursorState === 'text') {
      gsap.to(circleRef.current, {
        width: 80,
        height: 80,
        duration: 0.3,
        ease: 'power2.out',
      });
    } else {
      gsap.to(circleRef.current, {
        width: 40,
        height: 40,
        duration: 0.3,
        ease: 'power2.out',
      });
    }
  }, [cursorState]);

  if (isTouchDevice) return null;

  return (
    <>
      {/* Dot */}
      <div
        ref={dotRef}
        className="fixed pointer-events-none z-[10000] -translate-x-1/2 -translate-y-1/2 mix-blend-difference"
        style={{
          opacity: isVisible ? 1 : 0,
          transition: 'opacity 0.3s',
        }}
      >
        <div className="w-2 h-2 bg-white rounded-full" />
      </div>

      {/* Circle */}
      <div
        ref={circleRef}
        className="fixed pointer-events-none z-[9999] -translate-x-1/2 -translate-y-1/2 mix-blend-difference flex items-center justify-center"
        style={{
          width: 40,
          height: 40,
          opacity: isVisible ? 1 : 0,
          transition: 'opacity 0.3s',
        }}
      >
        <div className="w-full h-full border border-white rounded-full" />

        {/* Text inside cursor */}
        {cursorState === 'text' && cursorText && (
          <div
            ref={textRef}
            className="absolute text-xs font-medium text-white tracking-wider uppercase"
          >
            {cursorText}
          </div>
        )}
      </div>
    </>
  );
}
